import { lazy } from 'react';

import Browser from '@wetransfer/concorde-browser';

import Actions from '@app/store/action';
import Store from '@app/store';

import ErrorHandler from '@module/error-handler';
import I18n from '@module/i18n';
import Router from '@module/router';
import LaunchDarkly from '@module/launchdarkly';

// Static pages with code
import '@app/page/home';
import '@app/page/sign-in';
import '@app/page/plus';
import '@app/page/about';
import '@app/page/help';
import '@app/page/legal';
import '@app/page/products';
import CookiePage from '@app/page/legal/cookie';
import Preview from '@app/page/transfer/preview';
import StaticPage from '@component/panel/content';

import {
  shouldAskBeforeLeaving,
  createOnRouteHandler,
} from '@app/helper/route';

import { FF_IMPLEMENT_SPACES } from '@constant/experiments';
import { shouldDisplayUpsellProgressPanel } from '@app/helper/progress-panel';
import {
  isLoggedInPlusUser,
  isLoggedInFreeUser,
  isLoggedOutOrFreeUser,
  userHasFreeTier,
} from '@app/helper/user';
import { AUTH_STATE } from '@app/constant/auth';

const navigateToHomePage = (router) => {
  router.resolve('/', true);
};

const navigateToLoginPage = (router, path) => {
  router.resolve('/log-in');
  Store.dispatch(Actions.Session.setRedirect(path));
};

const redirectToLoginOrHomePage = (router, path, user) => {
  if (!user.loggedIn) {
    navigateToLoginPage(router, path);
    return;
  }

  if (userHasFreeTier(user)) {
    navigateToHomePage(router);
  }
};

const AdvertisePage = lazy(() =>
  import(/* webpackChunkName: "quote" */ '@app/page/advertise')
);

/**
 * A route can either be a string or an object with the following properties (not all properties are documented):
 * - `route` (required): The path.
 * - `component` (required): Which component to render on this route.
 * - `name`: The name of the route.
 * - `title`: The document title to use for this route.
 * - `shouldRedirect`: The user either has permission to view the page or not, a returned true value calls redirectTo, false let's them continue to the intended path.
 * - `redirectTo`: The route the user redirects to if they do not have permissions
 * `requiredAuthState`:
 *   - Set this to `AUTH_STATE.LOGGED_IN` if the route is only accessible to logged in users.
 *   - Set this to `AUTH_STATE.LOGGED_OUT` if the route is only accessible to logged out users.
 *   - Leave this undefined if users can access this route regardless of their logged state.
 */

export const getPageRoutes = () => [
  // Static pages
  {
    route: '/about',
    name: 'about-page',
    component: StaticPage,
    title: I18n.t('site.titles.about'),
  },
  {
    route: '/products',
    component: StaticPage,
    name: 'products',
    title: I18n.t('site.titles.apps'),
  },
  { route: '/legal/advertising', component: StaticPage },
  { route: '/legal/disclosure', component: StaticPage },
  { route: '/legal/privacy', component: StaticPage },
  { route: '/legal/cookies', component: CookiePage },
  { route: '/legal/takedown', component: StaticPage },
  { route: '/legal/terms', component: StaticPage },
  {
    route: '/pro',
    name: 'plus-page',
    component: StaticPage,
    title: I18n.t('site.titles.plus'),
    shouldRedirect: (user) => isLoggedInPlusUser(user),
    redirectTo: (router) => navigateToHomePage(router),
  },
  // Advertisement pages
  // For the advertise chunk, we can't use `advertise` as a name as it will be blocked
  // by the ad blockers.
  {
    route: '/advertise',
    component: AdvertisePage,
    name: 'advertise-home-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/contact',
    component: AdvertisePage,
    name: 'advertise-contact-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/gallery',
    component: AdvertisePage,
    name: 'advertise-gallery-page',
    title: I18n.t('site.titles.advertise'),
  },
  {
    route: '/advertise/guidelines',
    component: AdvertisePage,
    name: 'advertise-guidelines-page',
    title: I18n.t('site.titles.advertise'),
  },
  // Payment pages
  {
    route: '/payment/completed',
    name: 'user-onboarding',
    component: lazy(() =>
      import(/* webpackChunkName: "payment" */ '@app/page/payment')
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (router, path, user) =>
      redirectToLoginOrHomePage(router, path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
  },
  {
    route: '/payment/methods',
    name: 'payment-methods',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-methods" */ '@app/page/payment-methods'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (router, path, user) =>
      redirectToLoginOrHomePage(router, path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: '/payment/update',
    name: 'payment-update',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-update" */ '@app/page/payment-update'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (router, path, user) =>
      redirectToLoginOrHomePage(router, path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: '/payment/pending',
    name: 'payment-pending',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-pending" */ '@app/page/payment-pending'
      )
    ),
    shouldRedirect: (user) => !user.loggedIn,
    redirectTo: (router, path) => navigateToLoginPage(router, path),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: '/sign-up',
    component: lazy(() =>
      import(/* webpackChunkName: "sign-up" */ '@app/page/sign-up-page')
    ),
    name: 'sign-up-page',
    title: I18n.t('site.titles.sign_up'),
    shouldRedirect: (user) => user.loggedIn,
    redirectTo: (router) => navigateToHomePage(router),
    requiredAuthState: AUTH_STATE.LOGGED_OUT,
  },
  {
    route: '/upgrade',
    name: 'upgrade',
    component: lazy(() =>
      import(/* webpackChunkName: "upgrade" */ '@app/page/upgrade')
    ),
    shouldRedirect: (user) => !isLoggedInFreeUser(user),
    redirectTo: (router) => navigateToHomePage(router),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
];

export const init = () => {
  const transferRoutes = ['/wallpaper/:advert_id'];

  const pageRoutes = getPageRoutes();

  const plusRoutes = [
    {
      route: '/help',
      component: StaticPage,
      name: 'help-section',
      title: I18n.t('site.titles.help'),
    },
    {
      route: '/log-in',
      component: StaticPage,
      name: 'signin-page',
      title: I18n.t('site.titles.sign_in'),
      shouldRedirect: (user) => user.loggedIn,
      redirectTo: (router) => navigateToHomePage(router),
      requiredAuthState: AUTH_STATE.LOGGED_OUT,
    },
    {
      route: '/forgot-password',
      component: lazy(() =>
        import(
          /* webpackChunkName: "forgotpassword" */ '@app/page/forgotpassword'
        )
      ),
      title: I18n.t('site.titles.forgot_password'),
    },
    {
      route: '/ter-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "unsubscribe-expiration" */ '@app/page/unsubscribeexpiration'
        )
      ),
    },
    {
      route: '/pm-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "unsubscribe-onboarding" */ '@app/page/unsubscribe-onboarding'
        )
      ),
    },
    {
      route: '/profile',
      component: lazy(() =>
        import(/* webpackChunkName: "profile" */ '@app/page/profile')
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router) => navigateToLoginPage(router, '/profile'),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/security',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/your-apps',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/payment',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/payment/cancel',
      component: lazy(() =>
        import(
          /* webpackChunkName: "payment-cancel" */ '@app/page/payment-cancel'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/billing',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/invoices',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/renew-billing',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      payment: true,
    },
    {
      route: '/account',
      component: lazy(() =>
        import(/* webpackChunkName: "account" */ '@app/page/account')
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/contacts',
      component: lazy(() =>
        import(/* webpackChunkName: "contact-list" */ '@app/page/contact/list')
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/transfers',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-list" */ '@app/page/transfer/list'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/transfers/:group/:transferId',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-detail" */ '@app/page/transfer/detail'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/payment/retry',
      component: lazy(() =>
        import(
          /* webpackChunkName: "complete-order" */ '@app/page/complete-order'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      payment: true,
    },

    {
      route: '/transfer/progress',
      component: shouldDisplayUpsellProgressPanel(Store.getState().user)
        ? lazy(() =>
            import(
              /* webpackChunkName: "transfer-progress-upsell" */ '@app/page/transfer/progress-upsell'
            )
          )
        : lazy(() =>
            import(
              /* webpackChunkName: "transfer-progress" */ '@app/page/transfer/progress'
            )
          ),
      name: 'page-transfer-progress-details',
    },
    {
      route: '/confirm-receiving-email',
      component: lazy(() =>
        import(
          /* webpackChunkName: "confirm-receiving-email" */ '@app/page/receiving-email-confirmation'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/ltdn-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "ltdn-optout" */ '@app/page/link-transfer-unsubscribe'
        )
      ),
      auth: true,
    },
    {
      route: '/account/two-factor-authentication/enable',
      name: 'two-factor-authentication-enable',
      component: lazy(() =>
        import(
          /* webpackChunkName: "two-factor-authentication-enable" */ '@app/page/two-factor-authentication/enable'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/two-factor-authentication/generate-recovery-codes',
      component: lazy(() =>
        import(
          /* webpackChunkName: "two-factor-authentication-generate-recovery-codes" */ '@app/page/two-factor-authentication/generate-recovery-codes'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/two-factor-authentication',
      name: 'two-factor-authentication-sign-in',
      component: lazy(() =>
        import(
          /* webpackChunkName: "verify-code-sign-in" */ '@app/page/two-factor-authentication/verify-code-sign-in'
        )
      ),
      shouldRedirect: (user) => user.loggedIn,
      redirectTo: (router) => navigateToHomePage(router),
      requiredAuthState: AUTH_STATE.LOGGED_OUT,
    },
  ];

  // if viewing non mobile version, root path is transfers
  if (!Browser.isMobile) {
    transferRoutes.push('/');
    transferRoutes.push('/downloads/:id/:secret?');

    // /downloads/:id/:recipient_id/grid does NOT match this route
    transferRoutes.push('/downloads/:id/:recipient_id/:secret(\\w{6})');
    transferRoutes.push('/welcome');

    transferRoutes.push({
      route: '/account-verification',
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    plusRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
      component: Preview,
      name: 'preview-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });
    plusRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/grid',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-grid" */ '@app/page/transfer/grid'
        )
      ),
      name: 'grid-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });
  } else {
    const Transfer = lazy(() =>
      import(/* webpackChunkName: "transfer" */ '@component/transfer')
    );

    pageRoutes.push({
      route: '/',
      component: StaticPage,
      name: 'home-mobile',
    });

    pageRoutes.push({
      route: '/upload',
      component: Transfer,
      name: 'transfer-mobile',
    });
    pageRoutes.push({
      route: '/account-verification',
      component: Transfer,
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (router, path) => navigateToLoginPage(router, path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      name: 'transfer-mobile',
    });

    pageRoutes.push({ route: '/downloads/:id/:secret', component: Transfer });
    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
      component: Transfer,
    });
    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview',
      component: Transfer,
    });

    pageRoutes.push({
      route: '/downloads/:id/:recipient_id/:secret(\\w{6})',
      component: Transfer,
    });
    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/grid',
      component: Transfer,
      name: 'grid-panel',
      updateTransfer: true,
    });
  }
  if (LaunchDarkly.getVariant(FF_IMPLEMENT_SPACES)) {
    plusRoutes.push({
      route: '/projects',
      component: lazy(() =>
        import(/* webpackChunkName: "spaces-list" */ '../spaces-bundle/list')
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (router, path, user) =>
        redirectToLoginOrHomePage(router, path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
  }
  // Configure all available routes
  // Start the router
  const router = new Router({
    // The following routes affect the Transfer component
    transfer: transferRoutes,

    // The following routes affect the Page component, mid-width
    plus: plusRoutes,

    // The following routes affect the Page component, full-width
    page: pageRoutes,
  });

  // Let's see what we need to do for this new route
  router.on('route', createOnRouteHandler(router));

  // Catch errors / "404" = redirect to root
  router.on('error', (path) => {
    ErrorHandler.addMetadata('path', path);

    ErrorHandler.track({
      name: 'UnknownRoute',
      message: 'This route does not exist.',
      stack: 'init/routing',
    });

    router.resolve('/', false);
  });

  router.on('beforeroute', (complete) => {
    const { route, transfer } = Store.getState();

    // Prompt for something however you want and make sure that the complete
    // function is called when the user wants to continue.
    if (shouldAskBeforeLeaving(route, transfer)) {
      Store.dispatch(Actions.Route.setNextAction(complete));
    } else {
      complete();
    }

    // You MUST return false if you manually control when to continue
    return false;
  });
  router.start(window.location.pathname, {
    hijackLinks: true,
  });
};
